import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentApi, type AgentPreferences } from '@/api/agent';

const PREFERENCES_KEY = ['agent', 'preferences'] as const;

export function useAgentQuery(agentId: string) {
  return useQuery({
    queryKey: ['agent', 'agents', agentId],
    queryFn: async () => (await agentApi.getAgent(agentId)).data,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The account's credit balance. `enabled` exists so a surface that only needs the balance
 * to state a price — the brand plan's 150-credit Phase 3 — does not make every operator
 * pay for the request just by opening the workspace. The query key is shared, so a caller
 * that is already fetching it costs nothing extra.
 */
export function useCreditsQuery(enabled = true) {
  return useQuery({
    queryKey: ['agent', 'credits'],
    queryFn: async () => (await agentApi.getCredits()).data,
    staleTime: 30 * 1000,
    enabled,
  });
}

/** Honest usage summary (plan session + weekly consumption) for the settings panel. */
export function useUsageQuery() {
  return useQuery({
    queryKey: ['agent', 'usage'],
    queryFn: async () => (await agentApi.getUsage()).data,
    staleTime: 30 * 1000,
  });
}

/** Per-user agent preferences (active drafting model + model catalog). */
export function useAgentPreferences() {
  return useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => (await agentApi.getPreferences()).data,
    staleTime: 60 * 1000,
  });
}

export function useUpdateAgentPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activeModel: string | null) =>
      agentApi.updatePreferences({ activeModel }).then((r) => r.data),
    // Optimistic: reflect the new active model immediately.
    onMutate: async (activeModel) => {
      await qc.cancelQueries({ queryKey: PREFERENCES_KEY });
      const prev = qc.getQueryData<AgentPreferences>(PREFERENCES_KEY);
      if (prev) qc.setQueryData<AgentPreferences>(PREFERENCES_KEY, { ...prev, activeModel });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(PREFERENCES_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });
}
